import { useAbility } from '@casl/react'
import { Section } from '@react-stately/collections'
import { ItemElement } from '@react-types/shared'
import copy from 'copy-to-clipboard'
import { last } from 'lodash'
import { values } from 'mobx'
import { observer } from 'mobx-react-lite'
import { Key } from 'react'
import { useSelector } from 'react-redux'
import { generatePath, useHistory, useLocation } from 'react-router-dom'
import { useModals } from 'almanac-ui/Modal'
import { ArrowOut, Copy, Delete, Edit, Favorite, Link, Move, Share, Unfavorite } from 'almanac-ui/icons'
import { FavoritableType } from 'api/clients/FavoriteClient'
import { FolderClient } from 'api/clients/FolderClient'
import { UserService } from 'api/clients/UserService'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { AbilityContext } from 'components/Can'
import CopyModal from 'components/CopyModal/CopyModal'
import { useOnItemCopied } from 'components/CopyModal/useOnItemCopied'
import { Menu } from 'components/Menu/Menu'
import { Item } from 'components/Menu/MenuItem'
import DeleteModal from 'components/NavRightClickMenu/components/DeleteModal/DeleteModal'
import { useLoggedIn } from 'hooks/useLoggedIn'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { useWorkspace } from 'hooks/useWorkspace'
import { ALL_FILES_PATH, DESIGN_PATH } from 'paths'
import { useFinderRefresh } from 'screens/Finder/hooks/useFinderRefresh'
import { useRootStore } from 'stores/RootStore'
import { AlmanacFile, HandbookItemType, RootState, UserRole } from 'types'
import { getAlmanacUrlFor, getSearchString, redirectToSignup } from 'utils/urlUtils'
import { DesignMenuProps } from './Types'
import Styles from './DesignMenu.module.scss'
import { useQueryClient } from 'react-query'
import { useCreateFavorite } from 'hooks/queries/favorites/useCreateFavorite'
import { useDeleteFavorite } from 'hooks/queries/favorites/useDeleteFavorite'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { ChevronRightSmall, Share2, File02, Templates } from 'blaze-ui/icons'
import { DesignDownloadModal } from 'components/DesignDownloadModal/DesignDownloadModal'
import { ShareSheetDrawer } from 'common/ShareSheetV2/Drawer/ShareSheetDrawer'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { DesignDownloadDrawer } from 'components/DesignDownloadModal/DesignDownloadDrawer'
import { ShareSheetV2 } from 'common/ShareSheetV2/ShareSheetV2'
import { channelSupportsCaption } from 'components/Design/utils/channelSupportsCaption'
import { DesignChannel } from 'components/NewFileModal/Types'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'

const DesignMenu = observer(
  ({ file, hiddenKeys, onAction, menuType = 'dropdown', onDeleteSuccess, ...props }: DesignMenuProps) => {
    const workspace = useWorkspace(file.workspace.id)
    const history = useHistory()
    const location = useLocation()
    const { isMobile } = useResponsiveDesign()
    const { designStore, handbooks, drawersStore } = useRootStore()
    const isLoggedIn = useLoggedIn()
    const refreshFinder = useFinderRefresh()
    const openWindowSafely = useOpenWindowSafely()
    const { openModal, closeModal } = useModals()
    const createFavorite = useCreateFavorite()
    const deleteFavorite = useDeleteFavorite()
    const queryClient = useQueryClient()
    const canAccessAdminTemplates = useFlipperFlag('admin_templates')

    const isGenerationV2 = file.is_generation_v2

    const channel = file?.channel || undefined
    const caption = designStore?.captionsStore?.caption ?? ''
    const hasCaption = channel && channelSupportsCaption(channel as DesignChannel) && caption.trim().length > 0

    const { onItemCopied } = useOnItemCopied({
      title: file.title,
      folderSlug: last(file.ancestors)?.seo_string,
      handbooksStore: handbooks,
      fileType: 'design',
    })

    // State
    const favorite = useSelector((state: RootState) =>
      state.favorites.find(
        ({ favoritable_id, favoritable_type }) =>
          favoritable_id === file.id && favoritable_type === FavoritableType.Design,
      ),
    )
    const getCanonicalUrl = () => {
      const pathname = generatePath(DESIGN_PATH, { key: file.id })
      return getAlmanacUrlFor(pathname)
    }

    // Authorisation
    const ability = useAbility(AbilityContext)
    const authedFile = {
      // This is a workaround until we redesign how we expose non-accessible workspaces.
      role: workspace?.role || UserRole.UNAUTHED,
      permissions: file.permissions,
      __caslSubjectType__: 'Design',
    }

    const can: typeof ability.can = (action, subject) => {
      if (hiddenKeys?.has(action)) {
        return false
      }
      return ability.can(action, subject)
    }

    // Handlers
    const handleFavorite = () => {
      if (favorite) {
        deleteFavorite.mutate({ id: favorite.id, title: favorite.favoritable_title })
      } else {
        createFavorite.mutate({
          favoritableId: file.id,
          favoritableType: 'design',
          favoritableTitle: file.title,
        })
      }
    }

    const handleCopyLink = () => {
      copy(getCanonicalUrl())
      showToastMessage('Link copied')
    }

    const loadParentFolder = async () => {
      const parent = last(file.ancestors)
      let parentFolder: AlmanacFile | undefined

      if (parent) {
        const response = await FolderClient.getFolder(parent.id)
        if (response.type === SUCCESS_RESPONSE_TYPE) {
          parentFolder = response.data.folder
        }
      }

      return parentFolder
    }

    const handleCopy = async () => {
      if (!isLoggedIn) {
        const copyPath = encodeURIComponent(`${location.pathname}?${getSearchString({ copyTemplate: 'true' })}`)
        // Clear auth cookie, if not user will receive wrong_scope error and will not be able to log in
        UserService.unsetAuthCookie()
        redirectToSignup(history, { whenDoneRedirectTo: copyPath })
        return
      }

      const currentFolder = await loadParentFolder()

      openModal(CopyModal, {
        item: {
          id: file.id,
          title: file.title,
          header_emoji: file.emoji,
          linked_copies_count: 0,
        },
        itemType: 'design',
        folder: currentFolder,
        onItemCopied,
      })
    }

    const handleDelete = () => {
      const treeItem = values(handbooks.items).find(
        (item) => item.type === HandbookItemType.Document && item.data.document?.key === file.id,
      )

      openModal(DeleteModal, {
        files: [{ ...file, type: 'design' }],
        onRequest: () => {
          if (treeItem) {
            handbooks.removeItem(treeItem.id)
          }

          if (designStore?.fileId === file.id) {
            if (history.action === 'POP') {
              history.push(generatePath(ALL_FILES_PATH, { workspaceId: file.workspace.id }))
            } else {
              history.goBack()
            }
          }

          queryClient.invalidateQueries(calendarKeys.all)
          refreshFinder()
          onDeleteSuccess?.()

          closeModal()
        },
      })
    }

    const handleDownload = () => {
      if (isMobile) {
        drawersStore.closeAllDrawers()
        setTimeout(() => {
          drawersStore.openDrawer(DesignDownloadDrawer, {
            fileId: file.id,
          })
        }, 100)

        return
      }

      openModal(
        DesignDownloadModal,
        {
          fileId: file.id,
        },
        { newStack: true },
      )
    }

    const handleCopyToClipboard = () => {
      if (!!designStore?.captionsStore.caption?.length) {
        const clipboardItem = new ClipboardItem({
          'text/plain': new Blob([designStore.captionsStore.caption], { type: 'text/plain' }),
        })
        navigator.clipboard.write([clipboardItem]).then(() => {
          showToastMessage('Post content copied to your clipboard!')
        })
      }
    }

    const handleShare = () => {
      if (isMobile) {
        drawersStore.closeAllDrawers()
        setTimeout(() => {
          drawersStore.openDrawer(ShareSheetDrawer, {
            file: {
              id: file.id,
              type: 'design',
            },
          })
        }, 100)

        return
      }

      openModal(ShareSheetV2, {
        file: {
          id: file.id,
          type: file.type,
        },
      })
    }

    const handleTemplateAdmin = () => {
      designStore?.openTab('template_admin')
    }

    const handleAction = (action: Key) => {
      if (action === 'openInNewTab') {
        // eslint-disable-next-line no-restricted-properties
        openWindowSafely(isGenerationV2 ? window.location.href : getCanonicalUrl())
      }

      if (action === 'favorite') {
        handleFavorite()
      }

      if (action === 'copyLink') {
        handleCopyLink()
      }

      if (action === 'copy') {
        handleCopy()
      }

      if (action === 'move') {
        // NOOP: handled by the parent
      }

      if (action === 'delete') {
        handleDelete()
      }

      if (action === 'export') {
        handleDownload()
      }

      if (action === 'copyToClipboard') {
        handleCopyToClipboard()
      }

      if (action === 'rename') {
        // NOOP: handled by the parent
      }

      if (action === 'share') {
        handleShare()
      }

      if (action === 'templateAdmin') {
        handleTemplateAdmin()
      }

      onAction?.(action)
    }

    let disabledKeys = new Set(props.disabledKeys)

    let Chevron = menuType === 'tray' ? ChevronRightSmall : undefined

    return (
      <Menu
        menuType={menuType}
        onAction={handleAction}
        disabledKeys={disabledKeys}
        aria-label="Document Menu"
        className={Styles[menuType]}
        itemClassName={Styles.menuItem}
      >
        <Section>
          {
            [
              !hiddenKeys?.has('openInNewTab') && (
                <Item key="openInNewTab" textValue="Open in new tab" frontIcon={ArrowOut} endIcon={Chevron}>
                  Open in new tab
                </Item>
              ),
              can('move', authedFile) && (
                <Item key="move" textValue="Move" frontIcon={Move} endIcon={Chevron}>
                  Move to
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
        <Section>
          {
            [
              can('favorite', authedFile) && (
                <Item
                  key="favorite"
                  textValue={favorite ? 'Remove from Favorites' : 'Add to Favorites'}
                  frontIcon={favorite ? Unfavorite : Favorite}
                  endIcon={Chevron}
                >
                  {favorite ? 'Remove from Favorites' : 'Add to Favorites'}
                </Item>
              ),

              can('share', authedFile) && (
                <Item key="share" textValue="Share" frontIcon={Share} endIcon={Chevron}>
                  Share
                </Item>
              ),
              can('copyLink', authedFile) && (
                <Item key="copyLink" textValue="Copy Link" frontIcon={Link} endIcon={Chevron}>
                  Copy Link
                </Item>
              ),
              can('copy', authedFile) && (
                <Item key="copy" textValue="Make a Copy" frontIcon={Copy} endIcon={Chevron}>
                  Make a Copy
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>

        <Section>
          {
            [
              can('edit', authedFile) && canAccessAdminTemplates && (
                <Item key="templateAdmin" textValue="Template Admin" frontIcon={Templates} endIcon={Chevron}>
                  Template Admin
                </Item>
              ),
              can('export', authedFile) && (
                <Item key="export" textValue="Export Design" frontIcon={Share2} endIcon={Chevron}>
                  Export Design
                </Item>
              ),
              can('export', authedFile) && hasCaption && (
                <Item key="copyToClipboard" textValue="Copy Caption to Clipboard" frontIcon={File02} endIcon={Chevron}>
                  Copy Caption to Clipboard
                </Item>
              ),
              can('rename', authedFile) && (
                <Item key="rename" textValue="Rename" frontIcon={Edit} endIcon={Chevron}>
                  Rename
                </Item>
              ),
              can('delete', authedFile) && (
                <Item key="delete" textValue="Delete" frontIcon={Delete} endIcon={Chevron}>
                  Delete
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
      </Menu>
    )
  },
)

export { DesignMenu }
